import { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Download, Quote, Heart } from 'lucide-react';
import { toPng } from 'html-to-image';
import { toast } from 'react-toastify';
import { Quote as QuoteType } from '../../types';
import { generateQuoteImage } from '../../utils/imageUtils';
import { formatLikeCount } from '../../utils/formatUtils';

interface QuoteCardProps {
  quote: QuoteType;
  priority?: number;
}

export default function QuoteCard({ quote, priority = 0 }: QuoteCardProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const likes = JSON.parse(localStorage.getItem('quoteLikes') || '{}');
    setIsLiked(!!likes[quote.id]);
    // Generate a random number between 10,000 and 50,000 for initial likes
    const baseCount = Math.floor(Math.random() * 40000) + 10000;
    setLikeCount(likes[quote.id]?.count || baseCount);
  }, [quote.id]);

  const handleLike = () => {
    const likes = JSON.parse(localStorage.getItem('quoteLikes') || '{}');
    
    if (likes[quote.id]) {
      delete likes[quote.id];
      setIsLiked(false);
      setLikeCount(prev => prev - 1);
      toast.info('Removed from favorites');
    } else {
      likes[quote.id] = { 
        timestamp: Date.now(), 
        count: likeCount + 1 
      };
      setIsLiked(true);
      setLikeCount(prev => prev + 1);
      toast.success('Added to favorites');
    }
    
    localStorage.setItem('quoteLikes', JSON.stringify(likes));
  };

  const getQuoteStyle = () => {
    switch (quote.length) {
      case 'short':
        return 'text-lg md:text-2xl leading-relaxed';
      case 'medium':
        return 'text-base md:text-xl leading-relaxed';
      case 'long':
        return 'text-sm md:text-lg leading-relaxed';
      default:
        return 'text-base md:text-xl leading-relaxed';
    }
  };

  const downloadQuoteImage = async () => {
    if (!cardRef.current) return;
    
    setIsGenerating(true);
    try {
      const dataUrl = await generateQuoteImage({
        text: quote.text,
        category: quote.category,
        author: quote.author,
        length: quote.length
      });
      
      const link = document.createElement('a');
      link.download = `quote-${quote.id}.png`;
      link.href = dataUrl;
      link.click();
      
      toast.success('Quote image downloaded successfully!');
    } catch (error) {
      console.error('Failed to generate quote image:', error);
      toast.error('Failed to download quote image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: priority * 0.1 }}
      className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
    >
      <div className="relative overflow-hidden bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50 p-4 md:p-6 rounded-t-xl">
        <Quote className="text-purple-600 mb-4" size={24} />
        
        <div className="flex flex-col items-center text-center">
          <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-600 mb-4">
            {quote.category}
          </span>
          
          <p className={`font-serif ${getQuoteStyle()} text-gray-800 mb-4`}>
            {quote.text}
          </p>
          
          {quote.author && (
            <p className="text-gray-600 italic text-sm md:text-base">
              - {quote.author}
            </p>
          )}
        </div>
      </div>

      <div className="p-3 md:p-4 border-t border-gray-100 flex justify-between items-center">
        <button
          onClick={handleLike}
          className="flex items-center gap-1.5 text-sm"
        >
          <Heart
            size={16}
            className={`transition-colors ${
              isLiked ? 'fill-pink-500 text-pink-500' : 'text-gray-400 hover:text-pink-500'
            }`}
          />
          <span className="text-gray-600">
            {formatLikeCount(likeCount)}
          </span>
        </button>

        <button
          onClick={downloadQuoteImage}
          disabled={isGenerating}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
        >
          {isGenerating ? (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
            />
          ) : (
            <>
              <Download size={14} />
              <span className="hidden sm:inline">Download</span>
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
}